import React, { useState, useEffect } from "react";
import {
  MDBIcon,
  MDBTabs,
  MDBTabsItem,
  MDBTabsLink,
  MDBTabsContent,
  MDBTabsPane,
  MDBDropdown,
  MDBDropdownToggle,
  MDBDropdownMenu,
  MDBDropdownItem,
  MDBInput,
} from "mdb-react-ui-kit";
import { MDBCard, MDBListGroup, MDBListGroupItem } from "mdb-react-ui-kit";
import axios from "axios";
import { FormLabel, Box, Heading, Text, FormControl } from "@chakra-ui/react";

import { State, City } from "country-state-city";
import ReactLoading from "react-loading";
import { GEOAPIFY_API_KEY } from "../config/api_keys";

const SelectLocation = ({ onAddressSelect, initialLocation, initialAddress }) => {
  const apiKey = GEOAPIFY_API_KEY;
  const [iconsActive, setIconsActive] = useState("tab1");
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stateDropdownValue, setStateDropdownValue] = useState(initialLocation?.state || initialAddress?.state || null);
  const [stateCode, setStateCode] = useState(null);
  const [cityDropdownValue, setCityDropdownValue] = useState(initialLocation?.city || initialAddress?.city || null);
  const [areaValue, setAreaValue] = useState(initialLocation?.area || initialAddress?.area || "");
  const [address, setAddress] = useState(initialAddress || {});
  const [location, setLocation] = useState(initialLocation || {});
  const [addorloc, setAddorloc] = useState("location");
  // Add state for city coordinates
  const [cityCoordinates, setCityCoordinates] = useState(null);

  // Initialize with initial values if provided
  useEffect(() => {
    if (initialLocation || initialAddress) {
      // Set initial values
      const locationData = initialLocation || initialAddress || {};
      
      if (locationData.state) {
        setStateDropdownValue(locationData.state);
        // Find state code for the state name
        const stateObj = State.getStatesOfCountry("IN").find(state => state.name === locationData.state);
        if (stateObj) {
          setStateCode(stateObj.isoCode);
        }
      }
      
      if (locationData.city) {
        setCityDropdownValue(locationData.city);
      }
      
      if (locationData.area) {
        setAreaValue(locationData.area);
      }
      
      // Set the address and location state
      setAddress(locationData);
      setLocation(locationData);
      
      // Update parent with initial values after a short delay to ensure component is mounted
      setTimeout(() => {
        updateParent();
      }, 100);
    } else {
      // Update parent component with initial values
      updateParent();
    }
  }, []);
  
  // Effect to ensure cities are properly loaded when editing
  useEffect(() => {
    if (stateDropdownValue && !stateCode) {
      // If we have a state name but no state code, find the state code
      const stateObj = State.getStatesOfCountry("IN").find(state => state.name === stateDropdownValue);
      if (stateObj) {
        setStateCode(stateObj.isoCode);
      }
    }
  }, [stateDropdownValue, stateCode]);
  
  // Effect to update cities when state code changes
  useEffect(() => {
    if (stateCode) {
      // This will trigger the cities variable to update
      console.log("State code updated:", stateCode);
    }
  }, [stateCode]);

  // Function to update parent component with current values
  const updateParent = () => {
    if (onAddressSelect) {
      // Determine coordinates based on selection method
      let coordinates = null;
      if (addorloc === "address") {
        // GPS location - use latitude/longitude directly
        if (latitude && longitude) {
          coordinates = { latitude, longitude };
        }
      } else {
        // Manual location - use cityCoordinates if available
        if (cityCoordinates && cityCoordinates.latitude && cityCoordinates.longitude) {
          coordinates = cityCoordinates;
        } else if (latitude && longitude) {
          // Fallback to GPS coordinates if available
          coordinates = { latitude, longitude };
        }
      }

      console.log("Sending to parent:", {
        location,
        address,
        addorloc,
        coordinates,
        hasCoordinates: !!coordinates,
        cityCoordinates,
        latitude,
        longitude
      });
      
      onAddressSelect({
        location,
        address,
        addorloc,
        coordinates
      });
    }
  };

  const handleIconsClick = (value) => {
    if (value === iconsActive) {
      return;
    }
    setIconsActive(value);

    if (value === "tab2") {
      setLoading(true);
      setAddorloc("address");
      
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        setLoading(false);
        alert("Geolocation is not supported by this browser. Please use manual location selection.");
        return;
      }
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude);
          setLongitude(position.coords.longitude);
          setLoading(false);
          // Immediately update parent with coordinates
          updateParent();
        },
        (error) => {
          console.log("Geolocation error:", error);
          setLoading(false);
          
          let errorMessage = "Unable to get your location. ";
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage += "Please enable location services in your browser settings.";
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage += "Location information is unavailable. Please try again.";
              break;
            case error.TIMEOUT:
              errorMessage += "Location request timed out. Please try again.";
              break;
            default:
              errorMessage += "Please enable location services or enter your location manually.";
          }
          alert(errorMessage);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    } else {
      setLoading(false);
      setAddorloc("location");
      // Update parent when switching tabs
      updateParent();
    }
  };
  
  const states = State.getStatesOfCountry("IN");
  const cities =
    stateDropdownValue != null ? City.getCitiesOfState("IN", stateCode) : [];
    
  useEffect(() => {
    if (latitude && longitude) {
      setLoading(true);
      axios
        .get(
          `https://api.geoapify.com/v1/geocode/reverse?lat=${latitude}&lon=${longitude}&apiKey=${apiKey}`
        )
        .then((response) => {
          const features = response.data.features;
          if (features && features.length > 0) {
            const props = features[0].properties;
            const addressData = {
              state: props.state || "",
              city: props.city || "",
              area: props.suburb || props.district || "",
              postcode: props.postcode || "",
            };
            setAddress(addressData);
            setLoading(false);
            
            // Update location state as well to ensure consistency
            const locationData = {
              state: props.state || "",
              city: props.city || "",
              area: props.suburb || props.district || "",
              postcode: props.postcode || "",
            };
            setLocation(locationData);
            
            // Update parent with new address and coordinates immediately
            onAddressSelect({
              location: locationData,
              address: addressData,
              addorloc: "address",
              coordinates: { latitude, longitude }
            });
          } else {
            throw new Error("Location data not found");
          }
        })
        .catch((error) => {
          console.error("Geocoding error:", error);
          setLoading(false);
          
          let errorMessage = "Unable to get your location details. ";
          if (error.response) {
            // Server responded with error status
            if (error.response.status === 401) {
              errorMessage += "API key is invalid or expired.";
            } else if (error.response.status === 429) {
              errorMessage += "API rate limit exceeded. Please try again later.";
            } else {
              errorMessage += `Server error (${error.response.status}).`;
            }
          } else if (error.request) {
            // Network error
            errorMessage += "Network error. Please check your internet connection.";
          } else {
            // Other error
            errorMessage += "Please enter your location manually.";
          }
          
          alert(errorMessage);
        });
    }
  }, [latitude, longitude]);

  // Get coordinates for selected city
  useEffect(() => {
    if (stateDropdownValue && cityDropdownValue) {
      setLoading(true);
      // Geocode the city to get coordinates
      axios.get(`https://api.geoapify.com/v1/geocode/search?city=${encodeURIComponent(cityDropdownValue)}&state=${encodeURIComponent(stateDropdownValue)}&country=India&apiKey=${apiKey}`)
        .then(response => {
          const features = response.data.features;
          if (features && features.length > 0) {
            const coords = features[0].geometry.coordinates;
            // GeoJSON uses [longitude, latitude] format
            const newCityCoordinates = { 
              longitude: coords[0],
              latitude: coords[1]
            };
            setCityCoordinates(newCityCoordinates);
            setLoading(false);
            // Update parent when city coordinates are available
            // Use setTimeout to ensure state is updated
            setTimeout(() => {
              updateParent();
            }, 100);
          } else {
            console.warn("No coordinates found for selected city");
            setLoading(false);
            // Still update parent even without coordinates
            setTimeout(() => {
              updateParent();
            }, 100);
          }
        })
        .catch(error => {
          console.error("Error getting city coordinates:", error);
          setLoading(false);
          // Still update parent even if geocoding fails
          setTimeout(() => {
            updateParent();
          }, 100);
        });
    } else {
      // Reset coordinates when state/city is cleared
      setCityCoordinates(null);
    }
  }, [stateDropdownValue, cityDropdownValue]);

  const handleAddressChange = (event) => {
    const area = event.target.value;
    setAreaValue(area);
    
    if (stateDropdownValue && cityDropdownValue) {
      const locationData = {
        state: stateDropdownValue,
        city: cityDropdownValue,
        area: area,
      };
      setLocation(locationData);
      // Also update address to ensure consistency
      const addressData = {
        state: stateDropdownValue,
        city: cityDropdownValue,
        area: area,
      };
      setAddress(addressData);
      
      // Update parent with new location
      setTimeout(() => {
        updateParent();
      }, 100);
    }
  };

  return (
    <Box className="mt-3 mb-3">
      <FormLabel fontWeight="bold" mb={2}>Location Details*</FormLabel>
      
      <MDBTabs className="mb-3 mt-3">
        <MDBTabsItem>
          <MDBTabsLink
            onClick={() => handleIconsClick("tab1")}
            active={iconsActive === "tab1"}
          >
            <MDBIcon fas icon="search-location" className="me-2" />
            Select Location
          </MDBTabsLink>
        </MDBTabsItem>
        <MDBTabsItem>
          <MDBTabsLink
            onClick={() => handleIconsClick("tab2")}
            active={iconsActive === "tab2"}
          >
            <MDBIcon fas icon="map-marked-alt" className="me-2" /> Current
            Location
          </MDBTabsLink>
        </MDBTabsItem>
      </MDBTabs>

      <MDBTabsContent>
        <MDBTabsPane className="mt-2 mb-2" show={iconsActive === "tab1"}>
          <FormControl isRequired mb={3}>
            <FormLabel fontSize="sm">State</FormLabel>
            <MDBDropdown>
              <MDBDropdownToggle
                caret
                color="secondary"
                className="dropdown-toggle"
              >
                {stateDropdownValue || "Select State"}
              </MDBDropdownToggle>
              <MDBDropdownMenu className="dropdown-menu">
                {states.map((state) => (
                  <MDBDropdownItem
                    key={state.id}
                    onClick={() => {
                      setStateDropdownValue(state.name);
                      setStateCode(state.isoCode);
                      setCityDropdownValue(null); // Reset city when state changes
                      
                      // Update both location and address with new state
                      const updatedData = {
                        ...location,
                        state: state.name,
                        city: null
                      };
                      setLocation(updatedData);
                      setAddress(updatedData);
                      
                      // Update parent after state change
                      setTimeout(() => {
                        updateParent();
                      }, 100);
                    }}
                  >
                    {state.name}
                  </MDBDropdownItem>
                ))}
              </MDBDropdownMenu>
            </MDBDropdown>
          </FormControl>
          
          <FormControl isRequired mb={3}>
            <FormLabel fontSize="sm">City</FormLabel>
            <MDBDropdown>
              <MDBDropdownToggle
                caret
                color="secondary"
                className="dropdown-toggle"
                disabled={!stateDropdownValue}
              >
                {cityDropdownValue || "Select City"}
              </MDBDropdownToggle>
              <MDBDropdownMenu className="dropdown-menu">
                {cities.map((city) => (
                  <MDBDropdownItem
                    key={city.id}
                    onClick={() => {
                      setCityDropdownValue(city.name);
                      
                      // Update both location and address with new city
                      const locationData = {
                        ...location,
                        city: city.name
                      };
                      setLocation(locationData);
                      setAddress(locationData);
                      
                      // Don't call updateParent here - let the useEffect handle it after coordinates are fetched
                      // This ensures coordinates are available when parent is updated
                    }}
                  >
                    {city.name}
                  </MDBDropdownItem>
                ))}
              </MDBDropdownMenu>
            </MDBDropdown>
            
            {!stateDropdownValue && (
              <Text fontSize="xs" color="red.500" mt={1}>
                Please select a state first
              </Text>
            )}
          </FormControl>
          
          <FormControl mb={3}>
            <FormLabel fontSize="sm">Area Details</FormLabel>
            <MDBInput
              value={areaValue}
              onChange={handleAddressChange}
              label="Enter Area Details"
            />
          </FormControl>
          
          {(!stateDropdownValue || !cityDropdownValue) && (
            <Text color="orange.500" fontSize="sm" mt={2}>
              Please select both state and city to continue
            </Text>
          )}
        </MDBTabsPane>
        
        <MDBTabsPane show={iconsActive === "tab2"}>
          {loading ? (
            <div className="container text-center">
              <ReactLoading
                type="spin"
                color="green"
                height={"5%"}
                width={"5%"}
              />
              <Text mt={2}>Fetching your location...</Text>
            </div>
          ) : (
            <div className="row">
              <div className="col-md-8 mb-3">
                <MDBCard>
                  <MDBListGroup flush>
                    <MDBListGroupItem>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">State</small>
                        <small>{address.state || "Not available"}</small>
                      </div>
                    </MDBListGroupItem>
                    <MDBListGroupItem>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">City</small>
                        <small>{address.city || "Not available"}</small>
                      </div>
                    </MDBListGroupItem>
                    <MDBListGroupItem>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">Area</small>
                        <small>{address.area || "Not available"}</small>
                      </div>
                    </MDBListGroupItem>
                    <MDBListGroupItem>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">Pincode</small>
                        <small>{address.postcode || "Not available"}</small>
                      </div>
                    </MDBListGroupItem>
                  </MDBListGroup>
                </MDBCard>
                
                {(!address.state || !address.city) && !loading && (
                  <Text color="orange.500" fontSize="sm" mt={2}>
                    Location information is incomplete. Try again or use manual selection.
                  </Text>
                )}
              </div>
              <div className="col-md-4">
                {latitude && longitude ? (
                  <div className="embed-responsive embed-responsive-4by3">
                    <iframe
                      title="Your Location"
                      className="embed-responsive-item"
                      src={`https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d3507.743908551197!2d${longitude}!3d${latitude}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sin!4v1684765091573!5m2!1sen!2sin`}
                      allowFullScreen=""
                      loading="lazy"
                      style={{width: "100%", height: "200px", border: 0}}
                    ></iframe>
                  </div>
                ) : (
                  <Text>Map not available</Text>
                )}
              </div>
            </div>
          )}
        </MDBTabsPane>
      </MDBTabsContent>
    </Box>
  );
};

export default SelectLocation;